import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  HomePageTitle,
  HomePageWrapper,
  HomeContentColumn,
  TabsContentWrapper,
} from "../Home/HomeStyled";
import { fetchMySignatureList, fetchSentForSignatureList } from "../../slice/HomeSlice";
import { checkSession } from "../../slice/AuthSlice";
import ErrorToast from "../../components/Notification/ErrorToast";
import Loader from "../../components/Loader/Loader";
import SignatureTab from "../Home/Signature/SignatureTab";
import { getRowProps, handleErrorFactory, handleAPICalls } from "../Home/HomeUtils";

/**
 * E-Signatures, as its own page behind a left-sidebar icon (Matthew,
 * 2026-10-06: "put an icon in the lefthand sidebar with the signature
 * info similar to the VA e-submission page").
 *
 * When Home became My Work, the old Document Signature tab was folded
 * into the stack — but only the "needs my signature" half survived the
 * move. The list of what *I* sent out (who it is pending with, how far
 * along, chase it, cancel it) had no home at all. This page hosts the
 * existing signature tab container at /signatures with its own fetch
 * lifecycle, the same shape as VaSubmissionsPage.
 *
 * "Sent for Signature" is the default tab: that is the question people
 * come here to answer. "Needs My Signature" stays as the second tab so
 * the page is the complete picture, even though those rows also appear
 * in My Work.
 */
const SignaturesPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [error, setError] = useState([]);
  const [showNotification, setShowNotification] = useState(false);
  const [activeTab, setActiveTab] = useState("sentForSignature");

  const { mySignatureList, sentForSignatureList } = useSelector((state) => state.home);
  const { sessionChecked } = useSelector((state) => state.auth);

  const handleError = handleErrorFactory(setIsLoading, setError, setShowNotification);
  const callAPICalls = handleAPICalls(dispatch, setIsLoading, {
    fetchMySignatureList,
    fetchSentForSignatureList,
  });

  useEffect(() => {
    if (!sessionChecked) {
      dispatch(checkSession());
    }
    const loadInitialData = async () => {
      setIsInitialLoad(true);
      try {
        await callAPICalls("sentForSignature");
      } catch (err) {
        handleError(err?.message || "Failed to load signature requests");
      } finally {
        setIsInitialLoad(false);
      }
    };
    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, sessionChecked]);

  const handleTabChange = async (tab) => {
    setActiveTab(tab);
    await callAPICalls(tab);
  };

  const rowProps = getRowProps(navigate, dispatch, handleError);

  return (
    <HomePageWrapper>
      {isInitialLoad && isLoading && <Loader />}
      <HomeContentColumn>
        <HomePageTitle>E-Signatures</HomePageTitle>
      </HomeContentColumn>
      <TabsContentWrapper>
        <SignatureTab
          activeTab={activeTab}
          handleTabChange={handleTabChange}
          isLoading={isLoading}
          isInitialLoad={isInitialLoad}
          mySignatureList={mySignatureList}
          sentForSignatureList={sentForSignatureList}
          getRowProps={rowProps}
          setError={setError}
          setShowNotification={setShowNotification}
          handleError={handleError}
          setIsLoading={setIsLoading}
          handleAPICalls={callAPICalls}
        />
      </TabsContentWrapper>
      {showNotification && (
        <ErrorToast
          message={error}
          onClose={(id) =>
            setError((prev) => prev.filter((msg) => msg.id !== id))
          }
        />
      )}
    </HomePageWrapper>
  );
};

export default SignaturesPage;
